import {DateTime} from 'luxon';
import {getCurrentYear} from '../ui/util/util';
import i18n from '../i18n';

export enum DateTimeFormat {
    DATE = 'DATE',
    TIME = 'TIME',
    DATE_TIME = 'DATE_TIME',
    DATE_OR_TIME = 'DATE_OR_TIME',
    TIME_OR_DATE_TIME = 'TIME_OR_DATE_TIME',
    YEAR = 'YEAR',
}

export const minutesToMMSS = (seconds: number): string => {
    if (seconds <= 0) {
        return '00:00';
    }
    const minutesPart = Math.floor(seconds / 60);
    const secondsPart = seconds % 60;
    return `${minutesPart.toString()}m ${secondsPart.toFixed(0).toString()}s`;
};

export const formatDateTime = (datetime: string | undefined, format: DateTimeFormat): string => {
    if (!datetime) {
        return '';
    }
    const dt = DateTime.fromISO(datetime);
    const now = DateTime.local();
    switch (format) {
        case DateTimeFormat.DATE:
            return dt.toFormat('d.M.yyyy');
        case DateTimeFormat.TIME:
            return dt.toFormat('H:mm');
        case DateTimeFormat.DATE_TIME:
            return dt.toFormat('d.M.yyyy, H:mm');
        case DateTimeFormat.YEAR:
            return dt.toFormat('yyyy');
        case DateTimeFormat.DATE_OR_TIME:
            if (dt.hasSame(now, 'day')) {
                return dt.toFormat('H:mm');
            }
            return dt.toFormat('d.M.yyyy');
        case DateTimeFormat.TIME_OR_DATE_TIME:
            if (dt.hasSame(now, 'day')) {
                return dt.toFormat('H:mm');
            }
            return dt.toFormat('d.M.yyyy, H:mm');
        default:
            return dt.toFormat('d.M.yyyy H:mm');
    }
};

export function formatForInputDate(date: Date | string = new Date()): string {
    let current = date;

    if (typeof current === 'string') {
        current = new Date(date);
    }

    function padTo2Digits(num: number): string {
        return num.toString().padStart(2, '0');
    }

    return [current.getFullYear(), padTo2Digits(current.getMonth() + 1), padTo2Digits(current.getDate())].join('-');
}

export function getFutureDate(daysFromToday = 0): Date {
    const now = new Date();
    now.setDate(now.getDate() + daysFromToday);
    return now;
}

export const getYearData = (presentationCreationYear: string): {label: string; value: string}[] => {
    let year = getCurrentYear();
    const MIN_YEAR = parseInt(presentationCreationYear);

    const data: {label: string; value: string}[] = [];

    while (year >= MIN_YEAR) {
        data.push({label: year.toString(), value: year.toString()});
        year -= 1;
    }

    return data;
};

export function prettyDateFormat(date: Date, locale: string): string {
    const now = new Date();
    const timeOptions: Intl.DateTimeFormatOptions = {hour: '2-digit', minute: '2-digit'};
    const dateOptionsCurrentYear: Intl.DateTimeFormatOptions = {month: 'long', day: 'numeric'};
    const dateOptionsOtherYear: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'long', day: 'numeric'};

    // Helper to determine if two dates are on the same day
    const isSameDay = (d1: Date, d2: Date): boolean => d1.toDateString() === d2.toDateString();

    // Helper to determine if a date is "yesterday"
    const isYesterday = (d: Date): boolean => {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return isSameDay(d, yesterday);
    };

    // Helper to determine if a date is within the last 7 days
    const isWithinLast7Days = (d: Date): boolean => {
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return d > sevenDaysAgo;
    };

    // * Today - we use only HH:mm format
    if (isSameDay(date, now)) {
        return date.toLocaleTimeString(locale, timeOptions);
    }

    // * Yesterday - we only use 'yesterday'
    if (isYesterday(date)) {
        return i18n.t('dateTime.yesterday');
    }

    // * Withing last 7 days - we use '4 days ago'
    if (isWithinLast7Days(date)) {
        const weekdayOptions: Intl.DateTimeFormatOptions = {weekday: 'long'};
        return date.toLocaleDateString(locale, weekdayOptions);
    }

    // *  If it is this year - we use only day and month
    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString(locale, dateOptionsCurrentYear);
    }

    // * If it is not this year - we use day, month and year
    return date.toLocaleDateString(locale, dateOptionsOtherYear);
}

export function prettyTimeAgo(dateInput: Date | string, locale: string): string {
    const date = new Date(dateInput);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    const diffWeek = Math.floor(diffDay / 7);

    if (diffSec < 60) return i18n.t('dateTime.justNow');
    if (diffMin < 60) return i18n.t('dateTime.minutesAgo', {count: diffMin});
    if (diffHr < 24) return i18n.t('dateTime.hoursAgo', {count: diffHr});
    if (diffDay < 7) return i18n.t('dateTime.daysAgo', {count: diffDay});
    if (diffWeek < 5) return i18n.t('dateTime.weeksAgo', {count: diffWeek});

    const isSameYear = now.getFullYear() === date.getFullYear();
    const options: Intl.DateTimeFormatOptions = isSameYear
        ? {month: 'long', day: 'numeric'}
        : {month: 'long', day: 'numeric', year: 'numeric'};

    return new Intl.DateTimeFormat(locale, options).format(date);
}
